(() => {
  'use strict';
  const script = document.currentScript;
  const currentHandle = script?.dataset.currentHandle || '';
  if (customElements.get('sc-recently-viewed')) return;
  const key = 'sinocrafted_recently_viewed_v1';
  const legacyKey = 'sinocrafted_recently_viewed';
  const validHandle = /^[a-z0-9][a-z0-9-]*$/;
  const cache = new Map();
  let memory = [];

  function normalize(items) {
    if (!Array.isArray(items)) return [];
    return [...new Set(items.filter(item => typeof item === 'string').map(item => item.trim()).filter(item => validHandle.test(item)))].slice(0, 20);
  }

  function history() {
    try {
      const stored = localStorage.getItem(key);
      const items = JSON.parse(stored ?? localStorage.getItem(legacyKey) ?? '[]');
      memory = normalize(items);
      if (stored === null && memory.length) save(memory);
    } catch (_) {}
    return [...memory];
  }

  function save(items) {
    memory = normalize(items);
    try { localStorage.setItem(key, JSON.stringify(memory)); } catch (_) {}
  }

  function record() {
    const items = history();
    if (validHandle.test(currentHandle)) save([currentHandle, ...items]);
  }

  async function productData(root, currency, handle) {
    const cacheKey = `${root}:${currency}:${handle}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    const request = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          credentials: 'same-origin', signal: controller.signal
        });
        if (!response.ok) return null;
        const product = await response.json();
        if (!product?.title || !product.featured_image || !Number.isFinite(Number(product.price))) return null;
        const image = new URL(product.featured_image, location.origin);
        if (!['https:', 'http:'].includes(image.protocol)) return null;
        return { ...product, image: image.href, link: `${root}products/${encodeURIComponent(handle)}` };
      } catch (_) {
        return null;
      } finally {
        clearTimeout(timer);
      }
    })();
    cache.set(cacheKey, request);
    const product = await request;
    if (!product) cache.delete(cacheKey);
    return product;
  }

  record();

  class SCRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('[data-rv-track]');
      this.controls = this.querySelector('[data-rv-controls]');
      this.previous = this.querySelector('[data-rv-previous]');
      this.next = this.querySelector('[data-rv-next]');
      if (!this.track || !this.previous || !this.next) return;
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.updateControls(), { signal, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.goTo(event.key === 'Home' ? 0 : this.track.scrollWidth);
        }
      }, { signal });
      window.addEventListener('storage', event => {
        if (event.key === key || event.key === null) this.load();
      }, { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.load();
      }, { signal });
      this.observer = new ResizeObserver(() => {
        this.sizeImages();
        this.updateControls();
      });
      this.observer.observe(this.track);
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
      this.generation = (this.generation || 0) + 1;
    }

    async load() {
      const generation = this.generation = (this.generation || 0) + 1;
      const handle = this.dataset.currentHandle || currentHandle;
      const handles = history().filter(item => item !== handle);
      const limit = Math.min(20, Math.max(2, Number(this.dataset.limit) || 10));
      const root = new URL(this.dataset.rootUrl || '/', location.origin);
      if (root.origin !== location.origin) return;
      root.pathname = root.pathname.replace(/\/?$/, '/');
      const products = [];
      for (let start = 0; start < handles.length && products.length < limit;) {
        const batchSize = Math.min(5, limit - products.length);
        const batch = handles.slice(start, start + batchSize);
        start += batch.length;
        const found = await Promise.all(batch.map(item => productData(root.pathname, this.dataset.currency || 'USD', item)));
        if (!this.isConnected || this.generation !== generation) return;
        products.push(...found.filter(Boolean));
      }
      if (!this.isConnected || this.generation !== generation) return;
      const fragment = document.createDocumentFragment();
      products.forEach(product => fragment.appendChild(this.card(product)));
      this.track.replaceChildren(fragment);
      this.hidden = products.length === 0;
      this.track.scrollLeft = 0;
      this.sizeImages();
      this.updateControls();
    }

    card(product) {
      const item = document.createElement('li');
      item.className = 'sc-rv__item';
      const link = document.createElement('a');
      link.className = 'sc-rv__card';
      link.href = product.link;
      const media = document.createElement('span');
      media.className = 'sc-rv__media';
      const image = document.createElement('img');
      const url = new URL(product.image);
      const imageAt = width => { const sized = new URL(url); sized.searchParams.set('width', width); return sized.href; };
      image.src = imageAt(480);
      image.srcset = [100, 160, 240, 320, 480, 640, 800].map(width => `${imageAt(width)} ${width}w`).join(', ');
      image.sizes = '160px';
      image.width = 480;
      image.height = 480;
      image.alt = this.dataset.showTitle === 'true' ? '' : product.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      media.appendChild(image);
      link.appendChild(media);
      if (this.dataset.showTitle === 'true') {
        const title = document.createElement('span');
        title.className = 'sc-rv__title';
        title.textContent = product.title;
        link.appendChild(title);
      }
      if (this.dataset.showPrice !== 'false') link.appendChild(this.prices(product));
      item.appendChild(link);
      return item;
    }

    prices(product) {
      const group = document.createElement('span');
      group.className = 'sc-rv__price';
      const variants = Array.isArray(product.variants) ? product.variants.filter(variant => variant.price != null && Number.isFinite(Number(variant.price)) && Number(variant.price) >= 0) : [];
      const available = variants.filter(variant => variant.available !== false);
      const candidates = available.length ? available : variants;
      const variant = candidates.reduce((lowest, item) => !lowest || Number(item.price) < Number(lowest.price) ? item : lowest, null);
      const price = Number(variant ? variant.price : product.price);
      const compare = Number(variant ? variant.compare_at_price : product.compare_at_price);
      const onSale = Number.isFinite(compare) && compare > price && price >= 0;
      const currency = this.dataset.currency || 'USD';
      const locale = document.documentElement.lang || 'en';
      const money = amount => {
        try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount / 100); }
        catch (_) { return `${(amount / 100).toFixed(2)} ${currency}`; }
      };
      const current = document.createElement('span');
      current.className = 'sc-rv__price-current';
      current.textContent = money(price);
      current.setAttribute('aria-label', `${onSale ? this.dataset.saleLabel || 'Sale price' : this.dataset.regularLabel || 'Regular price'}: ${current.textContent}`);
      group.appendChild(current);
      if (onSale) {
        const original = document.createElement('s');
        original.className = 'sc-rv__price-compare';
        original.textContent = money(compare);
        original.setAttribute('aria-label', `${this.dataset.regularLabel || 'Regular price'}: ${original.textContent}`);
        const discount = document.createElement('span');
        discount.className = 'sc-rv__discount';
        const fraction = (compare - price) / compare;
        try {
          discount.textContent = `−${new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: fraction < .01 ? 2 : 0 }).format(fraction)}`;
        } catch (_) {
          discount.textContent = `−${Math.round(fraction * 100)}%`;
        }
        group.append(original, discount);
      }
      return group;
    }

    sizeImages() {
      const width = this.track.firstElementChild?.getBoundingClientRect().width;
      if (width) this.track.querySelectorAll('img').forEach(image => { image.sizes = `${Math.ceil(width)}px`; });
    }

    updateControls() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.abs(this.track.scrollLeft);
      this.controls.hidden = maximum < 2;
      this.previous.disabled = position < 2;
      this.next.disabled = position >= maximum - 2;
    }

    goTo(position) {
      const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollTo({ left: direction * position, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    move(direction) {
      const item = this.track.firstElementChild;
      if (!item) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item.getBoundingClientRect().width + gap;
      this.goTo(Math.max(0, Math.abs(this.track.scrollLeft) + direction * step));
    }
  }

  window.addEventListener('pageshow', event => { if (event.persisted) record(); });
  customElements.define('sc-recently-viewed', SCRecentlyViewed);
})();
